import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack, Typography, Chip, Paper, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { KpiCard, LoadingOverlay } from 'dashboard/components/common';

const ACTION_LABEL = {
  'user.enable': 'Enabled user',
  'user.disable': 'Disabled user',
  'user.password_reset': 'Sent password reset',
  'brand.enable': 'Enabled brand',
  'brand.disable': 'Disabled brand',
  'sub.cancel': 'Cancelled subscription',
  'sub.cancel.immediate': 'Cancelled (immediate)',
  'sub.resume': 'Resumed subscription',
  'sub.refund.full': 'Refunded (full)',
  'sub.refund.partial': 'Refunded (partial)',
  'domain.add': 'Added domain',
  'domain.delete': 'Deleted domain',
  'domain.verify.success': 'Domain verified',
  'domain.verify.fail': 'Domain verify failed',
  'data.viewed.export.users': 'Exported users CSV',
  'data.viewed.export.brands': 'Exported brands CSV',
  'data.viewed.export.domains': 'Exported domains CSV',
  'data.viewed.export.audit': 'Exported audit CSV',
};

const fmtRelative = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
};

const formatBytes = (n) => {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[u]}`;
};

const formatUptime = (s) => {
  if (!s && s !== 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
};

/**
 * Landing tab — platform-wide totals, service health, server stats.
 */
export default function OverviewTab() {
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [storage, setStorage] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      API.adminSummary({}).catch(() => null),
      API.adminHealth({}).catch(() => null),
      API.adminStorage({}).catch(() => null),
      API.adminListAudit({ limit: 6 }).catch(() => []),
    ]).then(([s, h, st, au]) => {
      if (cancelled) return;
      setSummary(s); setHealth(h); setStorage(st);
      setAudit(Array.isArray(au) ? au : []);
    }).catch((err) => toast.error(err?.message || 'Failed to load admin overview'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  return (
    <LoadingOverlay loading={loading && !summary} minHeight={240}>
      <Grid container spacing='16px'>
        <Grid item xs={6} md={3}>
          <KpiCard label='Users' value={summary?.users ?? '—'} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label='Brands' value={summary?.brands ?? '—'} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            label='Domains'
            value={summary?.domains ?? '—'}
            hint={summary?.domains_by_status
              ? `${summary.domains_by_status.verified || 0} verified · ${summary.domains_by_status.pending || 0} pending · ${summary.domains_by_status.failed || 0} failed`
              : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label='Errors (7d)' value={summary?.errors_7d ?? '—'} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px' }}>
            <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='10px'>
              Services
            </Typography>
            <Stack spacing='8px'>
              {(health?.services || []).map((s) => (
                <Stack key={s.name} direction='row' justifyContent='space-between' alignItems='center'>
                  <Stack direction='row' spacing='8px' alignItems='center'>
                    {s.ok ? (
                      <CheckCircleIcon color='success' fontSize='small' />
                    ) : (
                      <ErrorIcon color='error' fontSize='small' />
                    )}
                    <Typography fontWeight='600' textTransform='capitalize'>{s.name}</Typography>
                  </Stack>
                  <Typography variant='caption' color='text.secondary' sx={{ maxWidth: '60%', textAlign: 'right' }}>
                    {s.detail}
                  </Typography>
                </Stack>
              ))}
              {!health && (
                <Typography variant='body2' color='text.secondary'>Health probe unavailable.</Typography>
              )}
            </Stack>
            {health && (
              <Box mt='12px'>
                <Chip size='small' label={`Node ${health.node}`} sx={{ mr: '6px' }} />
                <Chip size='small' label={`Uptime ${formatUptime(health.uptime_seconds)}`} />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px' }}>
            <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='10px'>
              Server resources
            </Typography>
            <Stack spacing='6px'>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2'>Hostname</Typography>
                <Typography variant='body2' fontWeight='600'>{storage?.hostname || '—'}</Typography>
              </Stack>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2'>CPUs</Typography>
                <Typography variant='body2' fontWeight='600'>{storage?.cpus ?? '—'}</Typography>
              </Stack>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2'>Load (1m / 5m / 15m)</Typography>
                <Typography variant='body2' fontWeight='600'>
                  {storage ? `${storage.loadavg['1m'].toFixed(2)} / ${storage.loadavg['5m'].toFixed(2)} / ${storage.loadavg['15m'].toFixed(2)}` : '—'}
                </Typography>
              </Stack>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2'>Memory used</Typography>
                <Typography variant='body2' fontWeight='600'>
                  {storage ? `${formatBytes(storage.memory.used)} / ${formatBytes(storage.memory.total)}` : '—'}
                </Typography>
              </Stack>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='body2'>Disk used</Typography>
                <Typography variant='body2' fontWeight='600'>
                  {storage?.disk?.total != null
                    ? `${formatBytes(storage.disk.used)} / ${formatBytes(storage.disk.total)} (${formatBytes(storage.disk.free)} free)`
                    : 'Not available on this host'}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px' }}>
            <Stack direction='row' justifyContent='space-between' alignItems='center' mb='10px'>
              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700'>
                Recent activity
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Full history in the Audit log tab
              </Typography>
            </Stack>
            <Divider sx={{ mb: '10px' }} />
            {audit.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No admin actions yet.
              </Typography>
            ) : (
              <Stack spacing='6px'>
                {audit.map((e) => (
                  <Stack
                    key={e._id}
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    spacing='10px'
                  >
                    <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap>
                      <strong>{ACTION_LABEL[e.action] || e.action}</strong>
                      {e.target_label ? ` · ${e.target_label}` : ''}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                      {fmtRelative(e.createdAt)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </LoadingOverlay>
  );
}
